import { DateTime } from 'luxon';
import { ObjectiveEntry, ObjectiveTarget } from 'services/objectives/types';
import { toDateTime } from 'utils/date';

const DAY_MS = 24 * 60 * 60 * 1000;

const VIEW = {
  height: 200,
  padBottom: 28,
  padLeft: 48,
  padRight: 16,
  padTop: 16,
  width: 640,
};

interface ChartPoint {
  id: string;
  label: string;
  note: string;
  value: number;
  x: number;
  y: number;
}

interface ChartTick {
  label: string;
  value?: number;
  x: number;
  y: number;
}

interface ChartTargetPoint {
  label: string;
  value: number;
  x: number;
  y: number;
}

interface ChartTargetSeries {
  color: string;
  id: string;
  name: string;
  points: ChartTargetPoint[];
}

interface ProgressChart {
  band: { height: number; y: number } | null;
  goalY: number | null;
  height: number;
  plot: { height: number; width: number; x: number; y: number };
  points: ChartPoint[];
  targets: ChartTargetSeries[];
  width: number;
  xTicks: ChartTick[];
  yTicks: ChartTick[];
}

const TARGET_COLORS = [
  '#3d7ea6',
  '#c47b2b',
  '#8e5ea2',
  '#c45454',
  '#2f7d6d',
  '#5c6bc0',
  '#8d6e63',
  '#00838f',
];

const niceStep = (range: number, tickCount: number) => {
  const rough = range / Math.max(tickCount - 1, 1);
  if (!Number.isFinite(rough) || rough <= 0) {
    return 1;
  }
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  const nice = fraction >= 7.5 ? 10 : fraction >= 3 ? 5 : fraction >= 1.5 ? 2 : 1;
  return nice * power;
};

const DURATION_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400];

const durationStep = (range: number, tickCount: number) => {
  const rough = range / Math.max(tickCount - 1, 1);
  return DURATION_STEPS.find(step => step >= rough) ?? niceStep(range, tickCount);
};

const formatAxisValue = (value: number) => {
  const abs = Math.abs(value);
  const scaled = abs >= 1_000_000 ? value / 1_000_000 : abs >= 10_000 ? value / 1000 : value;
  const suffix = abs >= 1_000_000 ? 'M' : abs >= 10_000 ? 'k' : '';
  const digits = Math.abs(scaled) >= 100 || Number.isInteger(scaled) ? 0 : 1;
  const rounded = Number(scaled.toFixed(digits));
  return `${rounded}${suffix}`;
};

const formatAxisDate = (millis: number, span: number) => {
  const date = toDateTime(new Date(millis));
  if (!date?.isValid) {
    return '';
  }
  if (span < 36 * 60 * 60 * 1000) {
    return date.toFormat('HH:mm');
  }
  return date.toFormat('yyyy-MM-dd');
};

const formatPointLabel = (recordedAt: string) => (
  toDateTime(recordedAt)?.toFormat('yyyy-MM-dd HH:mm') ?? recordedAt
);

const buildProgressChart = ({
  entries,
  format,
  goal,
  goalTolerance,
  padLeft,
  targets,
  width,
}: {
  entries: ObjectiveEntry[];
  format?: string | null;
  goal?: number | null;
  goalTolerance?: number | null;
  padLeft?: number;
  targets?: ObjectiveTarget[] | null;
  width?: number;
}): ProgressChart | null => {
  const plotted = entries
    .map(entry => ({
      entry,
      time: DateTime.fromISO(entry.recordedAt).toMillis(),
    }))
    .filter(item => Number.isFinite(item.time) && Number.isFinite(item.entry.value))
    .sort((a, b) => a.time - b.time || a.entry.id.localeCompare(b.entry.id));
  const plottedTargets = (targets ?? [])
    .map((target, index) => ({
      color: TARGET_COLORS[index % TARGET_COLORS.length],
      id: target.id,
      name: target.name,
      points: target.points
        .map(point => ({
          label: formatPointLabel(point.at),
          time: DateTime.fromISO(point.at).toMillis(),
          value: point.value,
        }))
        .filter(point => Number.isFinite(point.time) && Number.isFinite(point.value))
        .sort((a, b) => a.time - b.time),
    }))
    .filter(target => target.points.length > 0);
  if (plotted.length === 0 && plottedTargets.length === 0) {
    return null;
  }

  const leftPad = padLeft && padLeft > 0 ? padLeft : VIEW.padLeft;
  const viewWidth = Math.max(
    leftPad + VIEW.padRight + 48,
    Math.round(width && Number.isFinite(width) && width > 0 ? width : VIEW.width),
  );
  const plot = {
    height: VIEW.height - VIEW.padTop - VIEW.padBottom,
    width: viewWidth - leftPad - VIEW.padRight,
    x: leftPad,
    y: VIEW.padTop,
  };
  const values = [
    ...plotted.map(item => item.entry.value),
    ...plottedTargets.flatMap(target => target.points.map(point => point.value)),
  ];
  const referenceValues = goal == null ? [] : [goal];
  if (goal != null && goalTolerance != null && goalTolerance > 0) {
    referenceValues.push(goal - goalTolerance, goal + goalTolerance);
  }
  let yMin = Math.min(...values, ...referenceValues);
  let yMax = Math.max(...values, ...referenceValues);
  if (yMin === yMax) {
    const pad = Math.abs(yMin) * 0.1 || 1;
    yMin -= pad;
    yMax += pad;
  } else {
    const pad = (yMax - yMin) * 0.12;
    yMin -= pad;
    yMax += pad;
  }
  const step = (format === 'duration' ? durationStep : niceStep)(yMax - yMin, 4);
  yMin = Math.floor(yMin / step) * step;
  yMax = Math.ceil(yMax / step) * step;
  if (yMin === yMax) {
    yMin -= step;
    yMax += step;
  }

  const times = [
    ...plotted.map(item => item.time),
    ...plottedTargets.flatMap(target => target.points.map(point => point.time)),
  ];
  let xMin = Math.min(...times);
  let xMax = Math.max(...times);
  const singleTime = xMax === xMin;
  if (singleTime) {
    xMin -= DAY_MS;
    xMax += DAY_MS;
  }
  const xSpan = xMax - xMin;

  const xInset = singleTime ? 0 : 12;
  const xOf = (time: number) => (
    plot.x + xInset + ((time - xMin) / xSpan) * (plot.width - (xInset * 2))
  );
  const yOf = (value: number) => plot.y + plot.height - ((value - yMin) / (yMax - yMin)) * plot.height;

  const yTickCount = Math.round((yMax - yMin) / step);
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, index) => {
    const value = Number((yMin + index * step).toFixed(8));
    return {
      label: formatAxisValue(value),
      value,
      x: plot.x,
      y: yOf(value),
    };
  });

  const tickTimes = singleTime
    ? [times[0]]
    : xSpan <= 14 * DAY_MS
      ? [xMin, xMax]
      : [xMin, xMin + xSpan / 2, xMax];
  const xTicks = tickTimes.map(time => ({
    label: formatAxisDate(time, singleTime ? 14 * DAY_MS : xSpan),
    x: xOf(time),
    y: plot.y + plot.height,
  }));

  const band = goal != null && goalTolerance != null && goalTolerance > 0
    ? {
      height: Math.abs(yOf(goal - goalTolerance) - yOf(goal + goalTolerance)),
      y: yOf(goal + goalTolerance),
    }
    : null;

  return {
    band,
    goalY: goal == null ? null : yOf(goal),
    height: VIEW.height,
    plot,
    points: plotted.map(item => ({
      id: item.entry.id,
      label: formatPointLabel(item.entry.recordedAt),
      note: item.entry.note?.trim() || '',
      value: item.entry.value,
      x: xOf(item.time),
      y: yOf(item.entry.value),
    })),
    targets: plottedTargets.map(target => ({
      color: target.color,
      id: target.id,
      name: target.name,
      points: target.points.map(point => ({
        label: point.label,
        value: point.value,
        x: xOf(point.time),
        y: yOf(point.value),
      })),
    })),
    width: viewWidth,
    xTicks,
    yTicks,
  };
};

export type { ProgressChart };
export { buildProgressChart };
